import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTourStore } from '@/stores/tourStore';
import { setTourNavigate, tourNavigate } from './tourNavigation';
import { DEEP_DIVE_CHAIN_ORDER, phaseHomeRoute } from './tourSteps';
import SpotlightOverlay from './SpotlightOverlay';
import { stepTarget, queryVisible, type TourStep } from './tourTypes';

/**
 * Keep only steps whose target element is currently in the DOM. Steps with no
 * target (centered welcome / wrap-up cards) always pass. This is the
 * graceful-skip guard: steps pointing at conditionally-rendered elements
 * (post-analysis form fields, generated post cards, empty-state lists) are
 * dropped instead of spotlighting a missing element.
 */
function resolveSteps(steps: TourStep[]): TourStep[] {
  return steps.filter((step) => {
    const sel = stepTarget(step);
    if (!sel) return true;
    // Targets the tour is about to bring into existence (e.g. the fields of a
    // modal the previous step gates the user into opening) aren't on the page
    // yet. Keep them — see `lateMount` in tourTypes.
    if (step.lateMount) return true;
    // Must be VISIBLE, not merely present: a `hidden lg:flex` copy of a control
    // is in the DOM at 0x0 and would otherwise pass this guard.
    return queryVisible(sel) !== null;
  });
}

/**
 * TourProvider — globally mounted host for the guided product tour.
 *
 * Owns the section-spotlight renderer (SpotlightOverlay) and drives it from
 * `tourStore` state. The tour modals (Welcome / Checkpoint / CloseConfirm /
 * EndScreen) are mounted separately in App.tsx.
 *
 * Mount this once near the app root, inside the router, so the tour works on
 * every page AND can perform the deep-dive cross-page navigation (see
 * tourNavigation.ts).
 */
export default function TourProvider() {
  const navigate = useNavigate();
  const location = useLocation();
  const advancedForRef = useRef<string | null>(null);

  // Register the router navigate fn so tour step callbacks (plain objects in
  // tourSteps.ts) can SPA-navigate between deep-dive sub-tours.
  useEffect(() => {
    setTourNavigate(navigate);
  }, [navigate]);

  const {
    isActive,
    currentPhase,
    currentSteps,
    showCloseConfirmModal,
    showCheckpointModal,
    showEndScreen,
  } = useTourStore(
    useShallow((s) => ({
      isActive: s.isActive,
      currentPhase: s.currentPhase,
      currentSteps: s.currentSteps,
      showCloseConfirmModal: s.showCloseConfirmModal,
      showCheckpointModal: s.showCheckpointModal,
      showEndScreen: s.showEndScreen,
    })),
  );

  // DOM-guard the step list for the active phase.
  const resolved = useMemo(() => resolveSteps(currentSteps), [currentSteps]);

  // Zero-step fallback: if every element-targeted step was filtered out, there
  // is nothing to spotlight. For deep-dive phases, advance to the next sub-tour
  // in the chain; for standalone phases, just end cleanly. Mirrors the old
  // driver.js behaviour so a page whose anchors never mounted can't strand the
  // tour on an empty overlay.
  useEffect(() => {
    if (!isActive || !currentPhase) {
      advancedForRef.current = null;
      return;
    }
    if (resolved.length > 0) return;
    // Guard against re-running the fallback for the same phase instance.
    if (advancedForRef.current === currentPhase) return;
    advancedForRef.current = currentPhase;

    const failedPhase = currentPhase;
    const chainIndex = DEEP_DIVE_CHAIN_ORDER.findIndex((e) => e.phase === failedPhase);
    if (chainIndex !== -1 && chainIndex < DEEP_DIVE_CHAIN_ORDER.length - 1) {
      const next = DEEP_DIVE_CHAIN_ORDER[chainIndex + 1];
      console.warn(`Tour: phase "${failedPhase}" resolved to zero steps — advancing to "${next.phase}"`);
      void useTourStore.getState().endTour(false);
      useTourStore.getState().setPendingPhase(next.phase);
      if (next.route && next.route !== location.pathname) {
        tourNavigate(next.route);
      }
    } else {
      console.warn(`Tour: phase "${failedPhase}" resolved to zero steps — ending tour (no chain successor).`);
      void useTourStore.getState().endTour(false);
    }
  }, [isActive, currentPhase, resolved.length, location.pathname]);

  // NOTE: navigating away does NOT end the tour. The user can detour mid-run
  // (e.g. "Manage workspaces" from the workspace switcher) — killing the
  // tutorial for that would be brutal. Instead the overlay PAUSES itself while
  // the current step's target isn't on the page (see SpotlightOverlay) and
  // resumes, on the same step, the moment they come back.

  // Intercept ESC the same way as the Skip button — route through the
  // confirmation modal instead of silently ending the tour.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const store = useTourStore.getState();
      if (!store.isActive || store.showCloseConfirmModal) return;
      e.preventDefault();
      store.openCloseConfirmModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Hide the spotlight while ANY tour modal is open so the dialog owns the
  // screen; when it closes with the tour still active, the overlay re-renders
  // at the preserved `currentStep` (resume is automatic — no saved index).
  const anyTourModalOpen = showCloseConfirmModal || showCheckpointModal || showEndScreen;

  if (!isActive || !currentPhase || anyTourModalOpen || resolved.length === 0) {
    return null;
  }
  // routeKey re-measures the spotlight after a navigation; resumeRoute lets the
  // paused bar send the user back to where the tour left off.
  return (
    <SpotlightOverlay
      steps={resolved}
      routeKey={location.pathname}
      resumeRoute={phaseHomeRoute(currentPhase)}
    />
  );
}

/**
 * useTour — ergonomic accessor for tour state + actions in pages/components.
 */
export function useTour() {
  return useTourStore(
    useShallow((s) => ({
      isActive: s.isActive,
      currentPhase: s.currentPhase,
      currentStep: s.currentStep,
      pendingPhase: s.pendingPhase,
      lastCompletedPhase: s.lastCompletedPhase,
      showWelcomeModal: s.showWelcomeModal,
      showCheckpointModal: s.showCheckpointModal,
      showCloseConfirmModal: s.showCloseConfirmModal,
      showEndScreen: s.showEndScreen,
      startTour: s.startTour,
      nextStep: s.nextStep,
      prevStep: s.prevStep,
      goToStep: s.goToStep,
      endTour: s.endTour,
      setPhase: s.setPhase,
      setSteps: s.setSteps,
      setPendingPhase: s.setPendingPhase,
      triggerCheckpoint: s.triggerCheckpoint,
      openWelcomeModal: s.openWelcomeModal,
      closeWelcomeModal: s.closeWelcomeModal,
      openCheckpointModal: s.openCheckpointModal,
      closeCheckpointModal: s.closeCheckpointModal,
      openCloseConfirmModal: s.openCloseConfirmModal,
      closeCloseConfirmModal: s.closeCloseConfirmModal,
      openEndScreen: s.openEndScreen,
      closeEndScreen: s.closeEndScreen,
      reset: s.reset,
    })),
  );
}
